import { getAbTestingData } from '@/api/public';
import logger from '@/utils/client-logger';
import { IS_CA } from '@/utils/constants';
import type { StateCreator } from 'zustand';

import type { Store } from '..';

const CA_LENS_FLOW_EXPERIMENT_NAME = 'ca_pending_lens_flow_test';
const CA_LENS_FLOW_EXPERIMENT_TIMEOUT_MS = 1500;

type CaLensFlowVariant = 'control' | 'experiment';
type CaLensFlowStatus = 'idle' | 'loading' | 'resolved' | 'fallback';

interface CaLensFlowAssignment {
  variant: CaLensFlowVariant;
  status: CaLensFlowStatus;
}

export interface CaLensFlowExperimentState {
  caLensFlowAssignment: CaLensFlowAssignment;
}

interface CaLensFlowExperimentActions {
  resolveCaLensFlowExperiment: (options?: { force?: boolean }) => Promise<CaLensFlowAssignment>;
}

export type CaLensFlowExperimentSlice = CaLensFlowExperimentState & CaLensFlowExperimentActions;

export const initialCaLensFlowExperimentState: CaLensFlowExperimentState = {
  caLensFlowAssignment: {
    variant: 'control',
    status: 'idle',
  },
};

let latestRequestId = 0;
let currentRequest: Promise<CaLensFlowAssignment> | null = null;

const withTimeout = <T>(request: Promise<T>, timeoutMs: number): Promise<T> =>
  new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(new Error(`CA lens flow experiment request timed out after ${timeoutMs}ms`));
    }, timeoutMs);

    request.then(
      (value) => {
        clearTimeout(timeoutId);
        resolve(value);
      },
      (error: unknown) => {
        clearTimeout(timeoutId);
        reject(error);
      },
    );
  });

export const createCaLensFlowExperimentSlice: StateCreator<
  Store,
  [],
  [],
  CaLensFlowExperimentSlice
> = (set, get) => ({
  ...initialCaLensFlowExperimentState,
  resolveCaLensFlowExperiment: async ({ force = false } = {}) => {
    if (!IS_CA) {
      const assignment: CaLensFlowAssignment = { variant: 'control', status: 'resolved' };
      set({ caLensFlowAssignment: assignment });
      return assignment;
    }

    if (!force && currentRequest) {
      return currentRequest;
    }

    if (!force && get().caLensFlowAssignment.status !== 'idle') {
      return get().caLensFlowAssignment;
    }

    const requestId = ++latestRequestId;
    set({ caLensFlowAssignment: { variant: 'control', status: 'loading' } });

    const request = (async () => {
      try {
        const response = await withTimeout(
          getAbTestingData({ param_name: CA_LENS_FLOW_EXPERIMENT_NAME }),
          CA_LENS_FLOW_EXPERIMENT_TIMEOUT_MS,
        );
        const value = response?.code === 200 ? response.data?.strategy_variable : null;
        let variant: CaLensFlowVariant | null = null;
        if (value === 1 || value === '1') {
          variant = 'control';
        } else if (value === 2 || value === '2') {
          variant = 'experiment';
        }
        const assignment: CaLensFlowAssignment = variant
          ? { variant, status: 'resolved' }
          : { variant: 'control', status: 'fallback' };

        if (requestId === latestRequestId) {
          set({ caLensFlowAssignment: assignment });
        }
        return assignment;
      } catch (error) {
        logger.warn('CA lens flow experiment request failed', error);
        const assignment: CaLensFlowAssignment = { variant: 'control', status: 'fallback' };
        if (requestId === latestRequestId) {
          set({ caLensFlowAssignment: assignment });
        }
        return assignment;
      }
    })();

    currentRequest = request;
    void request.finally(() => {
      if (currentRequest === request) {
        currentRequest = null;
      }
    });
    return request;
  },
});
